import { ExternalLink, MessageCircleIcon } from 'lucide-react';
import { Button } from '@rocco/components/button';
import { cn } from '@rocco/utils/cn';
import ActionCompletedIcon from '~/modules/tracking/component/ActionCompletedIcon';
import { useWebsiteReferralTracking } from '~/modules/tracking/util/websiteTracking';
import { useWebsiteVisited } from '~/modules/tracking/hook/useWebsiteVisited';
import { useEnquirySubmitted } from '~/modules/tracking/hook/useEnquirySubmitted';
import { useEnquiryModal } from '~/modules/enquiry/hooks/use-enquiry-modal';

type EnquiryModalConfig = NonNullable<Parameters<typeof useEnquiryModal>[0]>;

interface ProductHeaderCTAProps {
    /** The product page ID. */
    pageID: number;
    /** The professional's website URL or product-specific website URL. */
    website?: string | null;
    /** The CTA label from the professional's settings (e.g., "Request Price / Brochure"). */
    primaryCTALabel: string;
    /** Shared enquiry modal config from the product route. */
    enquiryModalConfig: EnquiryModalConfig;
    /** Whether to show the enquiry CTA button (EnquiryAudience gating). */
    showEnquiryCTA: boolean;
    className?: string;
}

/**
 * Compact above-the-fold CTA set for the product details page header,
 * mirroring the lower details block: Visit Website (secondary), then the
 * primary enquiry CTA. This block is additive — the full CTA set lower in
 * the details section is unchanged — and the two blocks are distinguished
 * in analytics by `Source` (ProductHeaderCTA vs ProductDetailsCTA), which
 * flows through EnquiryReveal → CreatedEnquiry and the Website referral
 * event.
 */
const ProductHeaderCTA = ({
    pageID,
    website,
    primaryCTALabel,
    enquiryModalConfig,
    showEnquiryCTA,
    className,
}: ProductHeaderCTAProps) => {
    const { referralTrackingURL, referralTrackingParams } =
        useWebsiteReferralTracking(website, {
            libraryItemID: pageID,
            professionalID: enquiryModalConfig.professional.ID,
            professionalTitle: enquiryModalConfig.professional.Title,
            source: 'ProductHeaderCTA',
        });

    const { hasVisited, markVisited } = useWebsiteVisited(pageID);
    const { hasSubmitted } = useEnquirySubmitted(pageID);
    const { openEnquiryModal } = useEnquiryModal({
        ...enquiryModalConfig,
        source: 'ProductHeaderCTA',
    });

    if (!showEnquiryCTA && !referralTrackingURL) {
        return null;
    }

    const buttonStyles = 'rounded-[5px] px-6 py-3 uppercase';
    const iconStyles = 'ml-2 h-4 w-4';

    return (
        <div className={cn('flex flex-wrap items-center gap-3', className)}>
            {referralTrackingURL && (
                <Button
                    color="secondary"
                    weight="default"
                    size="larger"
                    className={buttonStyles}
                    asChild
                >
                    <a
                        href={referralTrackingURL}
                        target="_blank"
                        {...referralTrackingParams}
                        rel="noopener"
                        onClick={markVisited}
                    >
                        Visit Website
                        <ActionCompletedIcon
                            isCompleted={hasVisited}
                            defaultIcon={
                                <ExternalLink className={iconStyles} />
                            }
                            className={iconStyles}
                        />
                    </a>
                </Button>
            )}
            {showEnquiryCTA && (
                <Button
                    color="dark"
                    weight="default"
                    size="larger"
                    className={buttonStyles}
                    onClick={() =>
                        openEnquiryModal({
                            title: primaryCTALabel,
                            ctaLabel: primaryCTALabel,
                        })
                    }
                >
                    {primaryCTALabel}
                    <ActionCompletedIcon
                        isCompleted={hasSubmitted}
                        defaultIcon={
                            <MessageCircleIcon className={iconStyles} />
                        }
                        className={iconStyles}
                    />
                </Button>
            )}
        </div>
    );
};

export default ProductHeaderCTA;
